import { useState, useEffect, useCallback, useRef } from 'react'
import {
  X, Search, BookOpen, Zap, Layout, LayoutTemplate, GitBranch,
  Upload, BarChart3, Code2, Tag, GitFork, Scissors, Database,
  TrendingUp, Cloud, Server, ShieldCheck, Archive, Download,
  AlertCircle, Wrench, HelpCircle, MessageSquare, Lock,
  ChevronDown, ChevronRight, ArrowLeft, Wand2,
} from 'lucide-react'
import { getNavGroups, getArticleById, getArticleContent, searchArticles } from './helpRegistry'
import SimpleMarkdown from './SimpleMarkdown'
import './HelpPanel.css'

// Map icon name strings from registry to lucide-react components
const ICON_MAP = {
  BookOpen, Zap, Layout, LayoutTemplate, GitBranch,
  Upload, BarChart3, Code2, Tag, GitFork, Scissors, Database,
  TrendingUp, Cloud, Server, ShieldCheck, Archive, Download,
  AlertCircle, Wrench, HelpCircle, MessageSquare, Lock,
}

function ArticleIcon({ name, size = 14 }) {
  const Icon = ICON_MAP[name]
  return Icon ? <Icon size={size} /> : null
}

// ── Nav component ──────────────────────────────────────────────────────────

function HelpNav({ activeId, onSelect, nodeGuideOpen, onToggleNodeGuide }) {
  const groups = getNavGroups()

  return (
    <nav className="hp-nav" aria-label="Help navigation">
      {groups.map(group => {
        const isNodeGuide = group.id === 'node-guide'
        const topLevel = isNodeGuide ? group.articles.filter(a => !a.isNode) : group.articles
        const nodeArticles = isNodeGuide ? group.articles.filter(a => a.isNode) : []

        return (
          <div key={group.id} className="hp-nav-group">
            <span className="hp-nav-group-label">{group.label}</span>

            {topLevel.map(article => (
              <button
                key={article.id}
                className={`hp-nav-item${activeId === article.id ? ' active' : ''}`}
                onClick={() => onSelect(article.id)}
              >
                <ArticleIcon name={article.icon} />
                <span>{article.title}</span>
              </button>
            ))}

            {/* Node sub-list under Node Guide */}
            {isNodeGuide && nodeArticles.length > 0 && (
              <>
                <button
                  className="hp-nav-item hp-nav-expand-btn"
                  onClick={onToggleNodeGuide}
                  aria-expanded={nodeGuideOpen}
                >
                  {nodeGuideOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  <span>Pipeline Nodes</span>
                  <span className="hp-nav-badge">{nodeArticles.length}</span>
                </button>
                {nodeGuideOpen && (
                  <div className="hp-nav-sub">
                    {nodeArticles.map((article, idx) => (
                      <button
                        key={article.id}
                        className={`hp-nav-item hp-nav-sub-item${activeId === article.id ? ' active' : ''}`}
                        onClick={() => onSelect(article.id)}
                      >
                        <span className="hp-nav-step-num">{idx + 1}</span>
                        <span>{article.title}</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )
      })}
    </nav>
  )
}

// ── Search results ─────────────────────────────────────────────────────────

function SearchResults({ results, query, onSelect }) {
  if (results.length === 0) {
    return (
      <div className="hp-search-empty">
        <p>No results for <strong>"{query}"</strong></p>
        <p className="hp-search-empty-hint">Try a different keyword, or browse the sections on the left.</p>
      </div>
    )
  }

  return (
    <div className="hp-search-results">
      <p className="hp-search-count">{results.length} result{results.length !== 1 ? 's' : ''} for &ldquo;{query}&rdquo;</p>
      {results.map(article => (
        <button
          key={article.id}
          className="hp-search-result-item"
          onClick={() => onSelect(article.id)}
        >
          <ArticleIcon name={article.icon} size={13} />
          <div className="hp-search-result-text">
            <span className="hp-search-result-title">{article.title}</span>
            <span className="hp-search-result-group">{article.groupLabel}</span>
          </div>
        </button>
      ))}
    </div>
  )
}

// ── Article view ───────────────────────────────────────────────────────────

function ArticleView({ articleId, onLaunchWizard, onNavigate }) {
  const article = getArticleById(articleId)
  const content = getArticleContent(articleId)
  const contentRef = useRef(null)

  // Scroll to top when article changes
  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0
  }, [articleId])

  if (!article) {
    return (
      <div className="hp-article-empty">
        <p>Article not found.</p>
      </div>
    )
  }

  return (
    <div className="hp-article" ref={contentRef}>
      <div className="hp-article-header">
        <div className="hp-article-meta">
          <span className="hp-article-group">{article.groupLabel}</span>
          {article.isNode && <span className="hp-article-node-badge">Pipeline Node</span>}
        </div>
      </div>

      <div className="hp-article-body">
        <SimpleMarkdown content={content} />

        {/* Quick Start: offer to launch the run wizard */}
        {articleId === 'quick-start' && onLaunchWizard && (
          <div className="hp-wizard-cta">
            <div className="hp-wizard-cta-text">
              <strong>Ready to run?</strong>
              <span>The Run Wizard walks you through uploading your source and starting the pipeline with guided prompts.</span>
            </div>
            <button className="hp-wizard-btn" onClick={onLaunchWizard}>
              <Wand2 size={14} />
              Launch Run Wizard
            </button>
          </div>
        )}
      </div>

      {/* Related navigation for node articles */}
      {article.isNode && (
        <NodeArticleNav currentId={articleId} onNavigate={onNavigate} />
      )}

      {/* Still need help? footer */}
      <div className="hp-article-footer">
        <span className="hp-article-footer-text">Still need help?</span>
        <button
          className="hp-article-footer-link"
          onClick={() => onNavigate('support')}
        >
          Contact & Support →
        </button>
      </div>
    </div>
  )
}

function NodeArticleNav({ currentId, onNavigate }) {
  const groups = getNavGroups()
  const nodeGroup = groups.find(g => g.id === 'node-guide')
  if (!nodeGroup) return null

  const nodes = nodeGroup.articles.filter(a => a.isNode)
  const currentIndex = nodes.findIndex(a => a.id === currentId)
  const prev = currentIndex > 0 ? nodes[currentIndex - 1] : null
  const next = currentIndex < nodes.length - 1 ? nodes[currentIndex + 1] : null

  if (!prev && !next) return null

  return (
    <div className="hp-node-nav">
      {prev ? (
        <button className="hp-node-nav-btn hp-node-nav-prev" onClick={() => onNavigate(prev.id)}>
          <ArrowLeft size={13} />
          <span>
            <small>Previous</small>
            {prev.title}
          </span>
        </button>
      ) : <span />}
      {next ? (
        <button className="hp-node-nav-btn hp-node-nav-next" onClick={() => onNavigate(next.id)}>
          <span>
            <small>Next</small>
            {next.title}
          </span>
          <ArrowLeft size={13} style={{ transform: 'rotate(180deg)' }} />
        </button>
      ) : <span />}
    </div>
  )
}

// ── Main panel ─────────────────────────────────────────────────────────────

export default function HelpPanel({ onClose, onLaunchWizard }) {
  const [activeId, setActiveId] = useState('overview')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState(null)
  const [nodeGuideOpen, setNodeGuideOpen] = useState(true)
  const searchRef = useRef(null)

  // Escape key closes
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const handleSearch = useCallback((value) => {
    setSearchQuery(value)
    if (!value.trim()) {
      setSearchResults(null)
    } else {
      setSearchResults(searchArticles(value))
    }
  }, [])

  const handleSelectArticle = useCallback((id) => {
    setActiveId(id)
    setSearchQuery('')
    setSearchResults(null)
    // Auto-expand node guide if a node is selected
    const article = getArticleById(id)
    if (article?.isNode || id === 'node-guide') setNodeGuideOpen(true)
  }, [])

  const handleLaunchWizard = useCallback(() => {
    onClose()
    if (onLaunchWizard) onLaunchWizard()
  }, [onClose, onLaunchWizard])

  return (
    <div
      className="hp-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Help Panel"
      onClick={onClose}
    >
      <div className="hp-panel" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="hp-header">
          <div className="hp-header-title">
            <BookOpen size={16} />
            <span>Help &amp; Guidance</span>
          </div>
          <div className="hp-header-search">
            <Search size={13} className="hp-search-icon" />
            <input
              ref={searchRef}
              className="hp-search-input"
              type="search"
              placeholder="Search help…"
              value={searchQuery}
              onChange={e => handleSearch(e.target.value)}
              aria-label="Search help articles"
            />
            {searchQuery && (
              <button
                className="hp-search-clear"
                onClick={() => handleSearch('')}
                aria-label="Clear search"
              >
                <X size={12} />
              </button>
            )}
          </div>
          <button className="hp-close" onClick={onClose} aria-label="Close help panel">
            <X size={16} />
          </button>
        </div>

        {/* Body: nav + content */}
        <div className="hp-body">

          {/* Left nav (always shown; search results replace article list) */}
          <div className="hp-sidebar">
            {searchResults !== null ? (
              <SearchResults
                results={searchResults}
                query={searchQuery}
                onSelect={handleSelectArticle}
              />
            ) : (
              <HelpNav
                activeId={activeId}
                onSelect={handleSelectArticle}
                nodeGuideOpen={nodeGuideOpen}
                onToggleNodeGuide={() => setNodeGuideOpen(o => !o)}
              />
            )}
          </div>

          {/* Right content */}
          <div className="hp-content">
            {searchResults !== null ? (
              // When searching, show a prompt to select a result
              searchResults.length > 0 ? (
                <div className="hp-search-prompt">
                  <Search size={32} className="hp-search-prompt-icon" />
                  <p>Select a result to read the article.</p>
                </div>
              ) : null
            ) : (
              <ArticleView
                articleId={activeId}
                onLaunchWizard={handleLaunchWizard}
                onNavigate={handleSelectArticle}
              />
            )}
          </div>

        </div>
      </div>
    </div>
  )
}
